import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatNumber,
  toNumber
} from './panel-utils.js';
import { createSvg, clearSvg, createSvgEl } from './chart-utils.js';

const DEFAULT_GRAPH = {
  nodes: [
    { id: 'core', name: 'core', loc: 1800, changed: true },
    { id: 'utils', name: 'utils', loc: 920 },
    { id: 'parser', name: 'parser', loc: 1260 },
    { id: 'render', name: 'render', loc: 2100, changed: true },
    { id: 'api', name: 'api', loc: 640 }
  ],
  edges: [
    { from: 'api', to: 'core' },
    { from: 'render', to: 'core' },
    { from: 'render', to: 'utils' },
    { from: 'parser', to: 'utils' }
  ]
};

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Code Dependency Explorer');
  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const graphWrap = document.createElement('div');
  graphWrap.className = 'ew-graph-canvas';
  const graphSvg = createSvg(320, 140);
  graphWrap.appendChild(graphSvg);

  const list = document.createElement('div');
  list.className = 'ew-list';

  body.append(metrics, graphWrap, list);

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Code Dependency Explorer');
    const graph = next?.data?.graph || next?.props?.graph || DEFAULT_GRAPH;
    const nodes = Array.isArray(graph.nodes) ? graph.nodes : DEFAULT_GRAPH.nodes;
    const edges = Array.isArray(graph.edges) ? graph.edges : DEFAULT_GRAPH.edges;

    metaEl.textContent = `${nodes.length} modules`;
    metrics.innerHTML = '';
    metrics.append(
      createMetric('modules', formatNumber(nodes.length)),
      createMetric('edges', formatNumber(edges.length)),
      createMetric('hot', formatNumber(nodes.filter((n) => n.changed).length))
    );

    renderGraph(graphSvg, nodes, edges);

    list.innerHTML = '';
    nodes.slice(0, 8).forEach((mod) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ew-list-item';
      const head = document.createElement('div');
      head.className = 'ew-list-head';
      const name = document.createElement('span');
      name.className = 'ew-list-title';
      name.textContent = mod.name;
      const tag = document.createElement('span');
      tag.className = 'ew-tag';
      tag.textContent = mod.changed ? 'changed' : 'stable';
      head.append(name, tag);
      const meta = document.createElement('div');
      meta.className = 'ew-list-meta';
      meta.textContent = `${formatNumber(toNumber(mod.loc, 0))} loc`;
      item.append(head, meta);
      item.addEventListener('click', () => {
        api.emit('node:input', { id: node.id, field: 'module', value: mod.id });
      });
      list.appendChild(item);
    });
  };

  update(node);
  return { el: root, update };
}

function renderGraph(svg, nodes, edges) {
  clearSvg(svg);
  if (!nodes.length) return;
  const width = 320;
  const height = 140;
  const pad = 16;
  const cols = Math.ceil(Math.sqrt(nodes.length));
  const rows = Math.ceil(nodes.length / cols);
  const gapX = (width - pad * 2) / Math.max(1, cols - 1);
  const gapY = (height - pad * 2) / Math.max(1, rows - 1);

  const positions = new Map();
  nodes.forEach((node, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = pad + col * gapX;
    const y = pad + row * gapY;
    positions.set(node.id, { x, y });
  });

  edges.forEach((edge) => {
    const from = positions.get(edge.from);
    const to = positions.get(edge.to);
    if (!from || !to) return;
    const line = createSvgEl('line', {
      x1: from.x,
      y1: from.y,
      x2: to.x,
      y2: to.y,
      stroke: 'var(--ew-ui-panel-border)',
      'stroke-opacity': 0.4
    });
    svg.appendChild(line);
  });

  nodes.forEach((node) => {
    const pos = positions.get(node.id);
    const circle = createSvgEl('circle', {
      cx: pos.x,
      cy: pos.y,
      r: node.changed ? 8 : 6,
      fill: node.changed ? 'var(--ew-ui-accent)' : 'var(--ew-ui-code-num)',
      'fill-opacity': node.changed ? 0.9 : 0.6
    });
    svg.appendChild(circle);
    const label = createSvgEl('text', {
      x: pos.x + 10,
      y: pos.y + 4,
      fill: 'var(--ew-ui-text)',
      'font-size': '9px'
    });
    label.textContent = node.name;
    svg.appendChild(label);
  });
}
